// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Alert, Button, Group, Loader, Modal, Select, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { formatHumanName } from '@medplum/core';
import type { Patient, Practitioner, Resource } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import {
  type AppointmentTypeDefinition,
  type AvailableTimeSlot,
  bookAppointment,
  resolveDefaultStaffPractitioner,
} from '@polarissleep/scheduling';
import { IconCircleCheck } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { getProviderAppEnvConfig } from '../../config/env';
import type { ActivePractitionerOption } from '../../hooks/useActivePractitioners';
import { useActivePractitioners } from '../../hooks/useActivePractitioners';
import { useAppointmentTypes } from '../../hooks/useAppointmentTypes';
import type { ProviderAppointmentCandidate } from '../../scheduling-next/types';
import { bookProviderV2Appointment } from '../../services/booking-service-v2';
import { showErrorNotification } from '../../utils/notifications';
import { withTenantScope } from '../../utils/tenant';
import { BookingConfirmation } from '../patient-appointments/BookingConfirmation';
import { SlotPicker } from '../patient-appointments/SlotPicker';

export interface ScheduleNextAppointmentModalProps {
  opened: boolean;
  onClose: () => void;
  patient: Patient;
}

export function ScheduleNextAppointmentModal({
  opened,
  onClose,
  patient,
}: ScheduleNextAppointmentModalProps): JSX.Element {
  const medplum = useMedplum();
  const { appointmentTypes, loading: typesLoading } = useAppointmentTypes();
  const { practitioners, loading: practitionersLoading, error: practitionersError } = useActivePractitioners();

  const [selectedTypeCode, setSelectedTypeCode] = useState<string | null>(null);
  const [selectedPractitionerId, setSelectedPractitionerId] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<AvailableTimeSlot | undefined>();
  const [selectedCandidate, setSelectedCandidate] = useState<ProviderAppointmentCandidate | undefined>();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [defaultStaffWarning, setDefaultStaffWarning] = useState<string | undefined>();
  const [resolvingDefaultStaff, setResolvingDefaultStaff] = useState(false);
  const [resolvedDefaultPractitioner, setResolvedDefaultPractitioner] = useState<Practitioner | undefined>();

  const appointmentType: AppointmentTypeDefinition | undefined = useMemo(
    () => appointmentTypes.find((t) => t.coding.code === selectedTypeCode),
    [appointmentTypes, selectedTypeCode]
  );
  const practitionerOptions = useMemo(() => {
    if (!resolvedDefaultPractitioner?.id) {
      return practitioners;
    }

    const alreadyPresent = practitioners.some((option) => option.value === resolvedDefaultPractitioner.id);
    if (alreadyPresent) {
      return practitioners;
    }

    const fallbackOption: ActivePractitionerOption = {
      value: resolvedDefaultPractitioner.id,
      label: resolvedDefaultPractitioner.name?.[0]
        ? formatHumanName(resolvedDefaultPractitioner.name[0])
        : 'Unnamed practitioner',
      practitioner: resolvedDefaultPractitioner,
    };

    return [...practitioners, fallbackOption].sort((left, right) => left.label.localeCompare(right.label));
  }, [practitioners, resolvedDefaultPractitioner]);

  useEffect(() => {
    if (!appointmentType) {
      return;
    }

    let active = true;
    setSelectedPractitionerId(null);
    setSelectedSlot(undefined);
    setSelectedCandidate(undefined);
    setDefaultStaffWarning(undefined);
    setResolvingDefaultStaff(true);
    setResolvedDefaultPractitioner(undefined);

    resolveDefaultStaffPractitioner(medplum, appointmentType.coding.code ?? '')
      .then((result) => {
        if (!active) {
          return;
        }

        if (result.status === 'resolved') {
          setResolvedDefaultPractitioner(result.practitioner);
          setSelectedPractitionerId(result.practitionerId);
        } else {
          setDefaultStaffWarning(
            'Default staff is not configured correctly for this appointment type. Select a staff member to continue.'
          );
        }
      })
      .catch(() => {
        if (active) {
          setDefaultStaffWarning(
            'Default staff is not configured correctly for this appointment type. Select a staff member to continue.'
          );
        }
      })
      .finally(() => {
        if (active) {
          setResolvingDefaultStaff(false);
        }
      });

    return () => {
      active = false;
    };
  }, [appointmentType, medplum]);

  const patientName = patient.name?.[0] ? formatHumanName(patient.name[0]) : 'this patient';

  const resetState = (): void => {
    setSelectedTypeCode(null);
    setSelectedPractitionerId(null);
    setSelectedSlot(undefined);
    setSelectedCandidate(undefined);
    setShowConfirmation(false);
    setError(undefined);
    setDefaultStaffWarning(undefined);
    setResolvedDefaultPractitioner(undefined);
  };

  const handleClose = (): void => {
    if (isSubmitting) {
      return;
    }
    resetState();
    onClose();
  };

  const handleTypeChange = (value: string | null): void => {
    setSelectedTypeCode(value);
    setSelectedPractitionerId(null);
    setSelectedSlot(undefined);
    setError(undefined);
    setDefaultStaffWarning(undefined);
    setResolvedDefaultPractitioner(undefined);
  };

  async function handleConfirm(): Promise<void> {
    const isV2 = getProviderAppEnvConfig().schedulingEngine === 'medplum-v2';
    if (!appointmentType || (isV2 ? !selectedCandidate : !selectedSlot)) {
      return;
    }
    setIsSubmitting(true);
    setError(undefined);
    try {
      if (isV2 && selectedCandidate) {
        await bookProviderV2Appointment(medplum, { patient, candidate: selectedCandidate, appointmentType });
      } else if (selectedSlot) {
        await bookAppointment(medplum, {
          patient,
          slotId: selectedSlot.slotId,
          appointmentTypeCode: appointmentType.coding.code ?? '',
          providerId: selectedSlot.providerId,
          start: selectedSlot.start,
          end: selectedSlot.end,
          appointmentType,
          decorate: <T extends { resourceType: string }>(r: T): T =>
            withTenantScope(r as unknown as Resource) as unknown as T,
        });
      }
      notifications.show({
        color: 'green',
        icon: <IconCircleCheck />,
        title: 'Success',
        message: 'Next appointment scheduled',
      });
      resetState();
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to schedule appointment.';
      setError(message);
      setShowConfirmation(false);
      showErrorNotification(err);
    } finally {
      setIsSubmitting(false);
    }
  }

  const selectedBooking = selectedCandidate ?? selectedSlot;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Stack gap={2}>
          <Text fw={700} size="lg">
            Schedule Next Appointment
          </Text>
          <Text size="sm" c="dimmed">
            For {patientName}
          </Text>
        </Stack>
      }
      size="xl"
      padding="xl"
      radius="md"
    >
      <Stack gap="md">
        <Select
          label="Appointment Type"
          placeholder={typesLoading ? 'Loading…' : 'Select appointment type'}
          data={appointmentTypes.map((t) => ({ value: t.coding.code ?? '', label: t.display }))}
          value={selectedTypeCode}
          onChange={handleTypeChange}
          disabled={typesLoading || isSubmitting}
          clearable
        />

        {defaultStaffWarning && (
          <Alert color="yellow" title="Staff selection required">
            {defaultStaffWarning}
          </Alert>
        )}

        {practitionersError && (
          <Alert color="red" title="Could not load staff">
            {practitionersError.message}
          </Alert>
        )}

        {appointmentType && (
          <Select
            label="Staff"
            placeholder={practitionersLoading || resolvingDefaultStaff ? 'Loading staff…' : 'Select staff'}
            data={practitionerOptions}
            value={selectedPractitionerId}
            onChange={(value) => {
              setSelectedPractitionerId(value);
              setSelectedSlot(undefined);
              setSelectedCandidate(undefined);
            }}
            disabled={practitionersLoading || resolvingDefaultStaff || isSubmitting}
          />
        )}

        {(practitionersLoading || resolvingDefaultStaff) && appointmentType && <Loader size="sm" />}

        {error && (
          <Alert color="red" title="Could not schedule">
            {error}
          </Alert>
        )}

        {appointmentType && selectedPractitionerId && getProviderAppEnvConfig().schedulingEngine === 'legacy' && (
          <SlotPicker
            appointmentType={appointmentType}
            practitionerId={selectedPractitionerId}
            selectedSlot={selectedSlot}
            onSlotSelected={setSelectedSlot}
          />
        )}

        {appointmentType && selectedPractitionerId && getProviderAppEnvConfig().schedulingEngine === 'medplum-v2' && (
          <SlotPicker
            engine="medplum-v2"
            appointmentType={appointmentType}
            practitionerId={selectedPractitionerId}
            selectedSlot={selectedCandidate}
            onSlotSelected={setSelectedCandidate}
          />
        )}

        <Group justify="flex-end" mt="md">
          <Button variant="subtle" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={() => setShowConfirmation(true)}
            disabled={
              !appointmentType ||
              (getProviderAppEnvConfig().schedulingEngine === 'medplum-v2' ? !selectedCandidate : !selectedSlot) ||
              isSubmitting
            }
          >
            Schedule
          </Button>
        </Group>
      </Stack>

      {appointmentType && selectedBooking && (
        <BookingConfirmation
          opened={showConfirmation}
          appointmentType={appointmentType}
          slot={selectedBooking}
          onConfirm={handleConfirm}
          onBack={() => setShowConfirmation(false)}
          isSubmitting={isSubmitting}
        />
      )}
    </Modal>
  );
}
